import bleEventsType from "../../consts/bleEventsType";
import bleExtensionMessageType from "../../consts/bleExtensionMessageType";
import { timeout } from "../../utils/utils";
import EventEmitter, { IBleEventArgs } from "./EventEmitter";
import {
    BleExtensionErrorCode,
    IBleCharacteristicNotifyStateResponse,
    IBleCharacteristicReadStoredDataResponse,
} from "./Models";
import PollingService from "./PollingService";
import { CharacteristicSubscriptionError } from "./errors/CharacteristicSubscriptionError";
import { ExtensionRequestError } from "./errors/ExtensionRequestError";
import { sendMessageAsync } from "./extensionUtils";

interface ICharacteristicEventData {
    isNotifying?: boolean;
    errorCode?: string;
    data?: string;
    timestamp?: any;
}

const READ_CHARACTERISTIC_DATA_TIMEOUT = 1000;

export default class BleDeviceServiceCharacteristic {
    private uuid: string;
    private serviceUuid: string;
    private deviceId: string;

    private messageEventEmitter = new EventEmitter<ICharacteristicEventData>();
    private readDataPoll = new PollingService();

    private isNotifyEnabled: boolean = false;

    constructor(deviceId: string, serviceUuid: string, uuid: string) {
        this.uuid = uuid;
        this.serviceUuid = serviceUuid;
        this.deviceId = deviceId;
    }

    writeValue(value: string) {
        return sendMessageAsync<any>({
            type: bleExtensionMessageType.writeCharacteristicValue,
            value,
            id: this.deviceId,
            service: this.serviceUuid,
            characteristic: this.uuid,
        });
    }

    triggerReadValue() {
        return sendMessageAsync<ICharacteristicEventData>({
            type: bleExtensionMessageType.readCharacteristicValue,
            id: this.deviceId,
            service: this.serviceUuid,
            characteristic: this.uuid,
        });
    }

    getCharacteristicValue() {
        return sendMessageAsync({
            type: bleExtensionMessageType.getCharacteristicValue,
            id: this.deviceId,
            service: this.serviceUuid,
            characteristic: this.uuid,
        });
    }

    readCharacteristicWriteValueErrorInternal() {
        return sendMessageAsync<ICharacteristicEventData>({
            type: bleExtensionMessageType.popStoredCharacteristicWriteValueError,
            id: this.deviceId,
            service: this.serviceUuid,
            characteristic: this.uuid,
        });
    }

    async subscribeOnMessage(handler: (arg0: IBleEventArgs<ICharacteristicEventData>) => void) {
        await this.subscribeOnMessageInternal();
        this.messageEventEmitter.subscribe(bleEventsType.characteristicMessage, handler);
    }

    unsubscribeOnMessage(handler: (arg0: IBleEventArgs<ICharacteristicEventData>) => void) {
        return this.unsubscribeOnMessageInternal(handler);
    }

    stopReading(): void {
        this.stopReadCharacteristicDataInternal();
    }

    private async subscribeOnMessageInternal() {
        if (this.isNotifyEnabled) return;

        await sendMessageAsync<any>({
            type: bleExtensionMessageType.setCharacteristicNotifyValue,
            notifyValue: true,
            service: this.serviceUuid,
            characteristic: this.uuid,
            id: this.deviceId,
        });

        let isNotifyEnabled = await this.checkIsNotifyEnabled();
        if (isNotifyEnabled) {
            this.setCharacteristicDataPolling(true);
        }
    }

    private async checkIsNotifyEnabled() {
        let i = 0;
        while (i < 3) {
            try {
                const { errorCode, isNotifying } = await sendMessageAsync<IBleCharacteristicNotifyStateResponse>({
                    type: bleExtensionMessageType.popStoredCharacteristicNotifyValue,
                    service: this.serviceUuid,
                    characteristic: this.uuid,
                    id: this.deviceId,
                });

                if (errorCode) {
                    throw new CharacteristicSubscriptionError(
                        `Error occurred during the characteristic [${this.uuid}] subscription`
                    );
                }

                this.isNotifyEnabled = isNotifying;

                if (isNotifying) {
                    break;
                }
            } catch (error) {
                if (error instanceof ExtensionRequestError) {
                    if (error.details?.errorCode === BleExtensionErrorCode.cantFindDataForTheRequest) {
                        await timeout(200);
                        i++;
                        continue;
                    }
                }

                throw new CharacteristicSubscriptionError(
                    `Error occurred during the characteristic [${this.uuid}] subscription. ${this.getErrorMessage(
                        error
                    )}`
                );
            }

            await timeout(200);
            i++;
        }

        return this.isNotifyEnabled;
    }

    private getErrorMessage(error: unknown) {
        if (error instanceof Error) return error.message;

        return String(error);
    }

    private async unsubscribeOnMessageInternal(handler: (arg0: IBleEventArgs<ICharacteristicEventData>) => void) {
        try {
            this.messageEventEmitter.unsubscribe(bleEventsType.characteristicMessage, handler);

            if(!this.messageEventEmitter.isEnySubscriptions()) {
                this.stopReadCharacteristicDataInternal();
                await sendMessageAsync<any>({
                    type: bleExtensionMessageType.setCharacteristicNotifyValue,
                    notifyValue: false,
                    service: this.serviceUuid,
                    characteristic: this.uuid,
                    id: this.deviceId,
                });
            }           
        } catch (error) {}
    }

    private async stopReadCharacteristicDataInternal(): Promise<void> {
        this.isNotifyEnabled = false;
        this.setCharacteristicDataPolling(false);
    }

    private async setCharacteristicDataPolling(isEnabled:boolean) {
        if (isEnabled) {
            !this.readDataPoll.getIsRunning() &&
                this.readDataPoll.startPoll(() => {
                    this.readCharacteristicDataInternal();
                }, READ_CHARACTERISTIC_DATA_TIMEOUT);
        } else {
            this.readDataPoll.stopPoll();
        }
    }

    private async readCharacteristicDataInternal(): Promise<void> {
        try {
            const { characteristicData, errorCode, timestamp } =
                await sendMessageAsync<IBleCharacteristicReadStoredDataResponse>({
                    type: bleExtensionMessageType.popStoredCharacteristicData,
                    service: this.serviceUuid,
                    characteristic: this.uuid,
                    id: this.deviceId,
                });

            if (characteristicData || errorCode) {
                this.messageEventEmitter.emit(bleEventsType.characteristicMessage, {
                    data: { data: characteristicData, errorCode: errorCode, timestamp },
                });
            }
        } catch (error) {}
    }
}
